import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Phone, Award, Users, Clock, Shield } from 'lucide-react';

const values = [
  {
    icon: Award,
    title: 'Craftsmanship',
    description: 'Every detail matters. We take pride in delivering work that stands the test of time.',
  },
  {
    icon: Users,
    title: 'Communication',
    description: 'Transparent, responsive, and always accessible. You\'ll never wonder about your project status.',
  },
  {
    icon: Clock,
    title: 'Reliability',
    description: 'We honor our commitments. When we say we\'ll be there, we\'re there. On time, every time.',
  },
  {
    icon: Shield,
    title: 'Respect',
    description: 'For your home, your time, and your investment. We treat every project as if it were our own.',
  },
];

export const About = () => {
  return (
    <main data-testid="about-page">
      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-[#F9F9F7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="w-16 h-px bg-[#C6A87C] mb-8" />
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl mb-6">About Us</h1>
            <p className="text-gray-600 text-lg leading-relaxed">
              Jax Remodeling Solutions brings together deep remodeling experience with professional project management and high-end client service standards. We're committed to elevating the remodeling experience in Northeast Florida.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/834892/pexels-photo-834892.jpeg?auto=compress&w=1200"
                  alt="Remodeling craftsmanship"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="w-12 h-px bg-[#C6A87C] mb-8" />
              <h2 className="font-heading text-3xl md:text-4xl mb-6">Our Story</h2>
              <div className="space-y-6 text-gray-600 leading-relaxed">
                <p>
                  Jax Remodeling Solutions was founded on a simple belief: homeowners deserve better. Better communication. Better craftsmanship. Better respect for their homes and their time.
                </p>
                <p>
                  Our senior lead brings over 19 years of hands-on remodeling experience across kitchens, bathrooms, and interior renovations. This deep expertise is complemented by our founder team's background in professional project management and construction coordination.
                </p>
                <p>
                  Together, we've created a company that combines the skill of seasoned craftsmen with the discipline of structured project delivery—delivering luxury results with white-glove service.
                </p>
                <p>
                  Based in St. Augustine and serving all of Northeast Florida, we're proud to bring our elevated approach to remodeling to homeowners throughout the region.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Experience Banner */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="font-heading text-4xl md:text-5xl text-[#C6A87C]">19+</span>
              <p className="text-white text-sm mt-2">Years Experience</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <span className="font-heading text-4xl md:text-5xl text-[#C6A87C]">100%</span>
              <p className="text-white text-sm mt-2">Commitment</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <span className="font-heading text-4xl md:text-5xl text-[#C6A87C]">24hr</span>
              <p className="text-white text-sm mt-2">Response Time</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <span className="font-heading text-4xl md:text-5xl text-[#C6A87C]">NE FL</span>
              <p className="text-white text-sm mt-2">Service Area</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 md:py-32 bg-[#F9F9F7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="w-16 h-px bg-[#C6A87C] mx-auto mb-6" />
            <h2 className="font-heading text-3xl md:text-4xl mb-4">Our Values</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              The principles that guide every project and every interaction.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8"
              >
                <div className="w-12 h-12 flex items-center justify-center border border-[#C6A87C]/30 mb-6">
                  <value.icon className="w-6 h-6 text-[#C6A87C]" />
                </div>
                <h3 className="font-heading text-xl mb-3">{value.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="w-12 h-px bg-[#C6A87C] mb-8" />
              <h2 className="font-heading text-3xl md:text-4xl mb-6">Why Choose Jax Remodeling</h2>
              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold mb-2">Clean Planning & Scheduling</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Structured project management ensures your renovation stays on track with clear milestones and realistic timelines.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Transparent Scope & Pricing</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Detailed estimates, clear change-order processes, and no hidden fees. You'll always know what to expect.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Dust Control & Jobsite Cleanliness</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    We protect your home throughout the project with barriers, floor coverings, and daily cleanup protocols.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">High-Touch Communication</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Regular updates via your preferred method, responsive to questions, and always accessible when you need us.
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1665069181618-5618c9b621ec?auto=format&fit=crop&w=1200&q=80"
                  alt="Quality craftsmanship"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-16 h-px bg-[#C6A87C] mx-auto mb-8" />
            <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">
              Let's Build Something Beautiful
            </h2>
            <p className="text-gray-400 text-lg mb-10">
              We'd love to hear about your project. Reach out to start the conversation.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="tel:9048636673"
                data-testid="about-call-btn"
                className="inline-flex items-center justify-center gap-3 bg-[#C6A87C] text-white px-8 py-4 text-xs font-semibold uppercase tracking-widest hover:bg-[#B09265] transition-colors"
              >
                <Phone className="w-4 h-4" />
                (904) 863-6673
              </a>
              <Link
                to="/contact"
                data-testid="about-contact-btn"
                className="inline-flex items-center justify-center gap-3 bg-white text-[#0A0A0A] px-8 py-4 text-xs font-semibold uppercase tracking-widest hover:bg-gray-100 transition-colors"
              >
                Contact Us
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};
